import { Decimal } from '@capsicum/common';
import { useMemo } from 'react';

import { useWCPledgeActivitySummary } from './useWCPledgeActivitySummary';
import { useWCPledgeRates } from './useWCPledgeRates';

export const useWCPledgeActivityTab = () => {
	const { usdRate } = useWCPledgeRates();
	const { isLoading, pledgeActivitySummary } = useWCPledgeActivitySummary();

	// User Pledges list
	const userPledges = useMemo(
		() =>
			pledgeActivitySummary.activePledges.map(pledge => ({
				...pledge,
				amount: new Decimal(pledge.amount).toFixed(2),
				bonus: {
					chz: new Decimal(pledge.bonus).toString(),
					fiat: new Decimal(pledge.bonus).mul(usdRate ?? 0).toString()
				},
				fiatAmount: new Decimal(pledge.amount).mul(usdRate || 0).toFixed(2)
			})),
		[pledgeActivitySummary.activePledges, usdRate]
	);

	// User Pledges after unstaking process
	const cancelledPledges = useMemo(
		() => ({
			claimable: pledgeActivitySummary.claimablePledges.map(pledge => ({
				...pledge,
				amount: new Decimal(pledge.amount).toFixed(2),
				bonus: {
					chz: new Decimal(pledge.bonus).toString(),
					fiat: new Decimal(pledge.bonus).mul(usdRate ?? 0).toString()
				},
				fiatAmount: new Decimal(pledge.amount).mul(usdRate ?? 0).toFixed(2)
			})),
			claimed: pledgeActivitySummary.claimedPledges.map(pledge => ({
				...pledge,
				amount: new Decimal(pledge.amount).toFixed(2),
				bonus: {
					chz: new Decimal(pledge.bonus).toString(),
					fiat: new Decimal(pledge.bonus).mul(usdRate ?? 0).toString()
				},
				fiatAmount: new Decimal(pledge.amount).mul(usdRate ?? 0).toFixed(2)
			})),
			unstaking: pledgeActivitySummary.unstakingPledges.map(pledge => ({
				...pledge,
				amount: new Decimal(pledge.amount).toFixed(2),
				bonus: {
					chz: new Decimal(pledge.bonus).toString(),
					fiat: new Decimal(pledge.bonus).mul(usdRate ?? 0).toString()
				},
				fiatAmount: new Decimal(pledge.amount).mul(usdRate ?? 0).toFixed(2)
			}))
		}),
		[
			pledgeActivitySummary.unstakingPledges,
			pledgeActivitySummary.claimablePledges,
			pledgeActivitySummary.claimedPledges,
			usdRate
		]
	);

	const { claimable, claimed, unstaking } = cancelledPledges;
	const hasCancelledPledges = claimable.length > 0 || unstaking.length > 0 || claimed.length > 0;

	const totalUnstaking = {
		chz: new Decimal(pledgeActivitySummary.totalUnstakingPledgesAmount),
		fiat: new Decimal(pledgeActivitySummary.totalUnstakingPledgesAmount).mul(usdRate ?? 0)
	};
	const totalClaimable = {
		chz: new Decimal(pledgeActivitySummary.totalClaimablePledgesAmount),
		fiat: new Decimal(pledgeActivitySummary.totalClaimablePledgesAmount).mul(usdRate ?? 0)
	};
	const totalClaimed = {
		chz: new Decimal(pledgeActivitySummary.totalClaimedPledgesAmount),
		fiat: new Decimal(pledgeActivitySummary.totalClaimedPledgesAmount).mul(usdRate ?? 0)
	};

	const totalPledgedFiat = new Decimal(pledgeActivitySummary.totalActivePledgesAmount || 0).mul(usdRate ?? 0);

	const totalAmounts = useMemo(
		() => ({
			claimable: {
				chz: totalClaimable.chz.toFixed(2),
				fiat: totalClaimable.fiat.toFixed(2)
			},
			claimed: {
				chz: totalClaimed.chz.toFixed(2),
				fiat: totalClaimed.fiat.toFixed(2)
			},
			pledged: {
				bonus: {
					chz: userPledges
						.reduce((acc, pledge) => acc.plus(new Decimal(pledge.bonus.chz)), new Decimal(0))
						.toString(),
					fiat: userPledges
						.reduce((acc, pledge) => acc.plus(new Decimal(pledge.bonus.fiat)), new Decimal(0))
						.toString()
				},
				chz: new Decimal(pledgeActivitySummary.totalActivePledgesAmount || 0).toFixed(2),
				fiat: totalPledgedFiat.toFixed(2)
			},
			unstaking: {
				chz: totalUnstaking.chz.toFixed(2),
				fiat: totalUnstaking.fiat.toFixed(2)
			}
		}),
		[
			pledgeActivitySummary.totalActivePledgesAmount,
			pledgeActivitySummary.totalUnstakingPledgesAmount,
			pledgeActivitySummary.totalClaimablePledgesAmount,
			pledgeActivitySummary.totalClaimedPledgesAmount,
			userPledges,
			usdRate
		]
	);

	const claimablePledgeItemsForTxPayload = cancelledPledges.claimable.map(claimablePledge => ({
		ftName: claimablePledge.team.ftName,
		teamId: claimablePledge.team.id
	}));

	return {
		cancelledPledges,
		claimablePledgeItemsForTxPayload,
		hasCancelledPledges,
		isLoading,
		totalAmounts,
		userPledges
	};
};
